import { Graph } from './graph';

export type TicketWithDependencies = {
  ticketConfigId: number;
  dependsOn: number[];
  unlimitedUse: boolean;
};

type InternalTicket = {
  position: number;
  ticketConfigId: number;
  dependsOn: number[];
};

export class TicketDependencyGraphBuilder {
  private graph: Graph;
  private sourceNode: number;
  private sinkNode: number;

  private tickets: InternalTicket[] = [];
  private ticketsByTc: Map<number, InternalTicket[]> = new Map();
  private unlimitedUseTcs: Set<number> = new Set();

  constructor(tickets: TicketWithDependencies[]) {
    this.graph = new Graph(tickets.length + 2);
    this.sourceNode = tickets.length;
    this.sinkNode = tickets.length + 1;
    this.preprocessTickets(tickets);
    this.fillGraph();
  }

  private preprocessTickets(tickets: TicketWithDependencies[]) {
    for (let i = 0; i < tickets.length; i++) {
      // Add graph node numbers to all tickets for easier calculations
      const ticket = {
        position: i,
        dependsOn: tickets[i].dependsOn,
        ticketConfigId: tickets[i].ticketConfigId
      };
      this.tickets.push(ticket);

      // arrange tickets by their ticket ids for faster access
      if (this.ticketsByTc.has(ticket.ticketConfigId)) {
        this.ticketsByTc.get(ticket.ticketConfigId).push(ticket);
      } else {
        this.ticketsByTc.set(ticket.ticketConfigId, [ticket]);
      }

      // Keep track of unlimited use ticket config IDs, since those will affect the edge values
      if (tickets[i].unlimitedUse) {
        this.unlimitedUseTcs.add(tickets[i].ticketConfigId);
      }
    }
  }

  /**
   * Adds connections between:
   * - the source node and all tickets which have dependencies
   * - the tickets with dependencies and their dependencies
   * - the sink node and all tickets upon which other tickets depend
   */
  private fillGraph() {
    for (let i = 0; i < this.tickets.length; i++) {
      // We can only increase the flow once per ticket to avoid bypassing the dependency checking
      // of the ticket configs with unlimited uses
      let flowIncreasedForTicket = false;
      const ticket = this.tickets[i];
      // find which tickets this one depends on and add connections
      for (const tcId of ticket.dependsOn) {
        // since this ticket has some tickets on which it depends on,
        // the source node is linked to it
        this.graph.addEdge(this.sourceNode, ticket.position);

        const antecedentTickets = this.ticketsByTc.get(tcId) ?? [];
        for (const at of antecedentTickets) {
          this.graph.addEdge(ticket.position, at.position);

          // since a ticket depends on this one, it is linked to the sink
          // this enables handling transient dependencies
          if (this.unlimitedUseTcs.has(at.ticketConfigId)) {
            if (!flowIncreasedForTicket) {
              flowIncreasedForTicket = true;
              // If a ticket is unlimited use, we still give the edge a finite value
              // to avoid bypassing the dependencies of the unlimited use ticket
              const currentWeight = this.graph.getEdge(
                at.position,
                this.sinkNode
              );
              this.graph.addEdge(at.position, this.sinkNode, currentWeight + 1);
            }
          } else {
            this.graph.addEdge(at.position, this.sinkNode);
          }
        }
      }
    }
  }

  build() {
    return {
      graph: this.graph,
      sourceNode: this.sourceNode,
      sinkNode: this.sinkNode
    };
  }
}
